// covered-by: frontends/carrie/src/hooks/__tests__/useAskPunttStream.test.ts
import { useCallback, useEffect, useRef, useState } from "react";

import { CHAT_WS_URL } from "../config/env";
import { getMessageStreamingState } from "../services/askPuntt";

/**
 * One settled check of a running warhol review: a reviewer, an evidence
 * verifier, or one deterministic check. Written by warhol as each one finishes
 * (`services/warhol/runtime/clients/ask_puntt_stream.py`), newest last; absent
 * from every rembrandt snapshot.
 */
export interface StreamingCheckProgress {
  /** The roster name of a subagent, or a detcheck pass's own name. */
  name: string;
  kind?: "reviewer" | "verifier" | "detcheck";
  status: "running" | "done" | "failed" | "timed_out" | "skipped";
  /** Why it is not `done` — the failure, or a skipped check's own reason. */
  reason?: string;
  /** When warhol says it settled, ISO-8601. */
  at: string;
}

/**
 * One finding an evidence verifier has accepted, so a comment can be shown
 * before the turn settles. Provisional — the turn's own `reviews` are final.
 */
export interface StreamingVerifiedFinding {
  intentId: string;
  severity: string | null;
  at: string;
  finding: unknown;
}

/**
 * Streaming message payload sent from the backend via WebSocket push.
 */
export interface StreamingState {
  status: string;
  content: string;
  streamingContent: string | null;
  agentSteps: unknown[];
  searchProgress: unknown[];
  canvasContent: unknown | null;
  canvasLoading: boolean;
  canvasChangedSectionIds: string[];
  viewTicketId: string | null;
  paneLabels: { context?: string; artwork?: string } | null;
  suggestedResponses: string[];
  projectData: unknown | null;
  projectLoading: boolean;
  contentBlocks: unknown[] | null;
  /**
   * Warhol only, and only while a review runs: what has settled so far — the
   * checklist the Ask V2 rail ticks off. Absent on every other turn, and a
   * reader must treat absent and empty alike.
   */
  checkProgress?: StreamingCheckProgress[];
  /** Warhol only: each finding an evidence verifier has accepted so far. */
  verifiedFindings?: StreamingVerifiedFinding[];
}

interface StreamUpdateMessage {
  type: "stream_update";
  chatId: string;
  messageId: string;
  payload: StreamingState;
}

/**
 * Return type of the useAskPunttStream hook.
 */
export interface AskPunttStreamResult {
  /** Latest streaming state from either WebSocket push or polling fallback. */
  streamingState: StreamingState | null;
  /** Whether the WebSocket is currently connected. */
  wsConnected: boolean;
  /** Whether the hook is using the HTTP polling fallback. */
  usingFallback: boolean;
  /**
   * Monotonic local timestamp (ms) of the last WebSocket push applied to
   * `streamingState`, or null when the latest value came from polling / nothing
   * has arrived. Lets the consumer prefer WS data only while it is fresh and
   * fall back to polling once the socket goes quiet (see chat.tsx merge).
   */
  lastWsPushAt: number | null;
}

// Global references — shared across all useAskPunttStream instances in the
// same tab so we maintain a single WebSocket connection, not one per hook.
let _globalWs: WebSocket | null = null;
let _globalWsReady = false;
// Map of "{chatId}:{messageId}" → SET of callbacks. A set (not a single
// callback) so two hook instances subscribed to the same chat/message don't
// clobber each other — the second would otherwise overwrite the first, and
// either unsubscribing would tear the shared subscription down for the other.
const _globalSubs = new Map<string, Set<(msg: StreamUpdateMessage) => void>>();
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
let _currentReconnectDelay = RECONNECT_DELAY_MS;
// Consecutive failed connections. After MAX_RECONNECT_ATTEMPTS we stop
// scheduling reconnects (the polling fallback carries the session) so a
// permanently-unreachable socket doesn't reconnect-storm forever.
let _consecutiveConnectFailures = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
let _reconnectsExhausted = false;
// Grace period before tearing down a subscription / closing the shared socket
// after the last subscriber for a key leaves. The hook's activation key
// (chatId/messageId/enabled) FLICKERS within a single turn — e.g. at the
// text→canvas handoff `streamingMessageId` briefly goes <id>→null→<id> and
// `enabled` toggles — and a previous version closed the socket + deleted the
// server subscription row on every such blip, reopening under a NEW
// connectionId and leaving a window where rembrandt's streaming pushes land on
// nobody (the empty-getSubscriptions no-op). Debouncing the teardown keeps ONE
// warm socket + ONE stable subscription row across these transient flips.
const IDLE_GRACE_MS = 5_000;
let _idleCloseTimer: ReturnType<typeof setTimeout> | null = null;
// Per-key deferred unsubscribe timers (keyed "{chatId}:{messageId}").
const _pendingKeyTeardown = new Map<string, ReturnType<typeof setTimeout>>();
// Liveness of the shared socket: the timestamp of the last frame received from
// the server (pong or stream_update). Module-level — keyed to the SOCKET, not
// to a hook instance — because the socket is shared. A per-instance copy would
// reset to null whenever a hook remounted onto an already-warm socket and make
// a healthy socket look unproven. `null` means "no frame yet" (the handshake
// completed but nothing has come back), which is deliberately treated as
// NOT-yet-alive so the poll covers the startup window.
let _lastFrameAt: number | null = null;
// Heartbeat sender, live only while the shared socket has subscribers.
let _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
// How often we re-evaluate socket health.
const HEALTH_CHECK_MS = 2_500;
// Liveness is proven by a HEARTBEAT, not by whether a push happens to arrive.
//
// History: `wsConnected` used to mean "OPEN *and* a stream_update within 5s".
// That cannot tell an IDLE-but-healthy socket from a half-open dead one, and
// the two are indistinguishable in normal use: a turn's pushes stop dead when
// it completes, and the background canvas phase writes Mongo without
// publishing to the snapshot, so NO pushes follow. Five seconds later the
// health check declared a perfectly good socket dead, armed the HTTP poll, and
// — because `wsConnected` can never recover without another push — left it
// polling /stream at 500ms for the rest of the session (observed in prod
// 2026-09-14: 48 polls over 40s of idle, plus ~12 more before each turn's
// first flush, all on an OPEN socket that never once closed).
//
// Now the client sends a cheap `{"action":"ping"}` while it has a live
// subscription; christo's $default handler answers `{type:"pong"}` (see
// websocket.defaultHandler). ANY inbound frame — a pong or a stream_update —
// proves the socket is alive. Polling therefore means what it should: the
// fallback for a socket that is genuinely dead (closed, errored, reconnect-
// exhausted, or no longer answering heartbeats). An idle socket stays
// "connected" and the redundant poll stays off.
//
// Keep HEARTBEAT_TIMEOUT_MS > 2 × HEARTBEAT_MS so one dropped/slow beat (or a
// cold $default Lambda) does not trigger a spurious fallback.
const HEARTBEAT_MS = 5_000;
const HEARTBEAT_TIMEOUT_MS = 12_000;
// Turn-scoped push-stall guard. The heartbeat proves the SOCKET works; it says
// nothing about the push PIPELINE behind it (rembrandt → SQS → push Lambda →
// PostToConnection). Those fail independently: the socket stays happily
// connected and answers pings while zero stream_updates arrive, and the turn
// silently stops updating. While a turn is actually generating we therefore
// still treat a long push silence as a degraded pipeline and fall back to
// polling — the one place the old push-recency rule was right.
//
// ONLY while generating (`expectPushes`). An idle socket, or the post-turn
// canvas phase, legitimately receives no pushes for minutes; treating those as
// degraded is the bug this file is fixing. The threshold sits far above any
// normal inter-push gap (a live turn pushes several times a second, and the
// captured prod turn's largest gap was 0.34s) but below the ~30s pre-first-
// token planning window a review turn can have.
// Exported so a caller that keeps its OWN safety net can tell "the socket is
// carrying this turn" from "the socket went quiet mid-turn" by the same
// threshold this guard uses, rather than a second number that could drift:
// `useAskV2Transport` relaxes the ask-v2 history poll exactly while a push has
// landed inside this window.
export const PUSH_STALL_MS = 20_000;
// Base interval for the HTTP polling fallback.
const POLL_BASE_MS = 500;
// Ceiling for the backed-off poll interval. A snapshot that is missing because
// the turn stalled (or because its 2h DynamoDB TTL lapsed while the message was
// still `pending`) never appears, so a flat 500ms poll would hammer /stream for
// as long as the tab stays open. Backing off to 8s bounds that to ~0.125 rps
// while still recovering within a few seconds once the snapshot does land.
const POLL_MAX_MS = 8_000;
// Consecutive failures tolerated before the poll interval starts backing off.
// The first few are expected (the snapshot may not have been written yet), so
// a healthy turn never sees the interval move off POLL_BASE_MS.
const POLL_FAILURES_BEFORE_BACKOFF = 3;
// Track whether WS URL is configured
let _wsUrlChecked = false;
let _wsUrl: string | null = null;

function getWsUrl(): string | null {
  if (!_wsUrlChecked) {
    const raw = CHAT_WS_URL;
    if (raw && typeof raw === "string" && raw.trim()) {
      _wsUrl = raw.trim();
    }
    _wsUrlChecked = true;
  }
  if (!_wsUrl) return null;
  // The API Gateway WebSocket $connect handler authorizes the connection from
  // a JWT passed on the URL — browsers can't attach the auth cookie/header to a
  // cross-origin WS handshake. Append the current token so the server can
  // resolve the user and gate subscriptions to chats they may access.
  // Recomputed each call (not cached) so a refreshed token is always used.
  let token: string | null = null;
  try {
    token = localStorage.getItem("token");
  } catch {
    // localStorage unavailable (SSR / privacy mode) — connect without a token;
    // the server will reject and the hook falls back to polling.
  }
  if (!token) return _wsUrl;
  const sep = _wsUrl.includes("?") ? "&" : "?";
  return `${_wsUrl}${sep}token=${encodeURIComponent(token)}`;
}

/** Total number of live subscription callbacks across all keys. */
function totalSubCount(): number {
  let n = 0;
  _globalSubs.forEach((set) => {
    n += set.size;
  });
  return n;
}

function cancelIdleClose(): void {
  if (_idleCloseTimer) {
    clearTimeout(_idleCloseTimer);
    _idleCloseTimer = null;
  }
}

function stopHeartbeat(): void {
  if (_heartbeatTimer) {
    clearInterval(_heartbeatTimer);
    _heartbeatTimer = null;
  }
}

// Send a `ping` on a fixed cadence while anyone is subscribed, so liveness is
// proven continuously instead of inferred from push traffic. Only runs with an
// active subscription: with none, the socket is torn down by the idle-grace
// timer anyway, so this costs nothing while the tab sits idle.
function startHeartbeat(): void {
  stopHeartbeat();
  _heartbeatTimer = setInterval(() => {
    const ws = _globalWs;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (totalSubCount() === 0) return;
    try {
      ws.send(JSON.stringify({ action: "ping" }));
    } catch {
      // send() throws only if the socket is closing — onclose handles it.
    }
  }, HEARTBEAT_MS);
}

// Debounced close: only actually closes if NObody has re-subscribed by the time
// the grace period elapses. A returning subscriber cancels it (see subscribeWs).
function scheduleIdleClose(): void {
  cancelIdleClose();
  _idleCloseTimer = setTimeout(() => {
    _idleCloseTimer = null;
    if (totalSubCount() === 0) {
      closeGlobalWs();
    }
  }, IDLE_GRACE_MS);
}

function cancelKeyTeardown(subKey: string): void {
  const t = _pendingKeyTeardown.get(subKey);
  if (t) {
    clearTimeout(t);
    _pendingKeyTeardown.delete(subKey);
  }
}

// Debounced server-side unsubscribe for one key. Deferring it means a key that
// flickers off then back on within the grace window keeps its server
// subscription row, so streaming pushes in that window are still delivered.
function scheduleKeyTeardown(
  subKey: string,
  chatId: string,
  messageId: string,
): void {
  cancelKeyTeardown(subKey);
  const timer = setTimeout(() => {
    _pendingKeyTeardown.delete(subKey);
    const set = _globalSubs.get(subKey);
    // Only tear down if the key is STILL empty when the grace elapses.
    if (set && set.size === 0) {
      _globalSubs.delete(subKey);
      const activeWs = _globalWs;
      if (activeWs && activeWs.readyState === WebSocket.OPEN) {
        activeWs.send(
          JSON.stringify({ action: "unsubscribe", chatId, messageId }),
        );
      }
    }
  }, IDLE_GRACE_MS);
  _pendingKeyTeardown.set(subKey, timer);
}

function closeGlobalWs(): void {
  cancelIdleClose();
  stopHeartbeat();
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
  if (_globalWs) {
    try {
      _globalWs.close();
    } catch {
      // ignore — already closing/closed
    }
  }
  _globalWs = null;
  _globalWsReady = false;
  _lastFrameAt = null;
}

function ensureWsConnection(): WebSocket | null {
  const url = getWsUrl();
  if (!url) return null;
  if (_reconnectsExhausted) return null;

  if (_globalWs && _globalWsReady && _globalWs.readyState === WebSocket.OPEN) {
    return _globalWs;
  }

  // Already connecting — wait for it
  if (_globalWs && _globalWs.readyState === WebSocket.CONNECTING) {
    return _globalWs;
  }

  const ws = new WebSocket(url);
  _globalWs = ws;
  // A brand-new socket has proven nothing yet — not alive until it answers.
  _lastFrameAt = null;

  ws.onopen = () => {
    _globalWsReady = true;
    _currentReconnectDelay = RECONNECT_DELAY_MS;
    _consecutiveConnectFailures = 0;
    // Re-subscribe all active subscriptions
    _globalSubs.forEach((_set, key) => {
      const [chatId, messageId] = key.split(":");
      if (chatId && messageId) {
        ws.send(JSON.stringify({ action: "subscribe", chatId, messageId }));
      }
    });
    startHeartbeat();
  };

  ws.onmessage = (event: MessageEvent) => {
    try {
      const raw = JSON.parse(event.data as string) as Record<string, unknown>;
      // ANY well-formed frame proves the socket is alive — a `pong` answering
      // our heartbeat counts exactly as much as a `stream_update`. Record it
      // before the stream_update filter below (which ignores pongs).
      _lastFrameAt = Date.now();
      if (
        raw.type === "stream_update" &&
        typeof raw.chatId === "string" &&
        typeof raw.messageId === "string"
      ) {
        const subKey = `${raw.chatId}:${raw.messageId}`;
        const callbacks = _globalSubs.get(subKey);
        if (callbacks) {
          const msg = raw as unknown as StreamUpdateMessage;
          // Snapshot before dispatch so a callback that unsubscribes mid-loop
          // doesn't mutate the set we're iterating.
          Array.from(callbacks).forEach((cb) => cb(msg));
        }
      }
    } catch {
      // Malformed message — ignore
    }
  };

  ws.onclose = () => {
    _globalWsReady = false;
    // Dead socket — stop beating and drop the liveness proof so the poll
    // re-arms until a reconnect answers again.
    stopHeartbeat();
    _lastFrameAt = null;
    if (totalSubCount() === 0) {
      // Nobody is listening — don't reconnect.
      return;
    }
    _consecutiveConnectFailures += 1;
    if (_consecutiveConnectFailures >= MAX_RECONNECT_ATTEMPTS) {
      // Give up on the socket; callers fall back to polling.
      _reconnectsExhausted = true;
      return;
    }
    // Schedule reconnect with exponential backoff
    if (_reconnectTimer) clearTimeout(_reconnectTimer);
    _reconnectTimer = setTimeout(() => {
      if (totalSubCount() > 0) {
        ensureWsConnection();
      }
    }, _currentReconnectDelay);
    _currentReconnectDelay = Math.min(
      _currentReconnectDelay * 1.5,
      MAX_RECONNECT_DELAY_MS,
    );
  };

  ws.onerror = () => {
    // onclose will fire after this, handling reconnect/backoff.
  };

  return ws;
}

function subscribeWs(
  chatId: string,
  messageId: string,
  onUpdate: (msg: StreamUpdateMessage) => void,
): () => void {
  const subKey = `${chatId}:${messageId}`;
  // A (re)subscribe cancels any pending grace-period teardown: keep the warm
  // socket and the existing server subscription row instead of closing/deleting
  // and reopening under a new connectionId (which orphans pushes mid-turn).
  cancelIdleClose();
  cancelKeyTeardown(subKey);
  let set = _globalSubs.get(subKey);
  if (!set) {
    set = new Set();
    _globalSubs.set(subKey, set);
  }
  set.add(onUpdate);

  // A fresh subscription means we want the socket alive again even if a prior
  // run exhausted reconnects.
  _reconnectsExhausted = false;
  _consecutiveConnectFailures = 0;

  const ws = ensureWsConnection();
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ action: "subscribe", chatId, messageId }));
  }
  // If CONNECTING, onopen re-subscribes everything.

  // Return unsubscribe function
  return () => {
    const current = _globalSubs.get(subKey);
    if (current) {
      current.delete(onUpdate);
      if (current.size === 0) {
        // Defer the server-side unsubscribe (and the local key removal) under a
        // grace window. The activation key flickers within a turn; deleting the
        // subscription row immediately would drop streaming pushes that arrive
        // before the re-subscribe lands. cancelKeyTeardown (on re-subscribe)
        // keeps the row alive across the flicker.
        scheduleKeyTeardown(subKey, chatId, messageId);
      }
    }
    // When the very last subscriber leaves, close the shared socket — but only
    // after a grace period, so a transient teardown doesn't churn the socket
    // (new connectionId per blip) and leave windows with no live connection.
    if (totalSubCount() === 0) {
      scheduleIdleClose();
    }
  };
}

/**
 * Real-time streaming hook for AskPuntt messages.
 *
 * Connects to the API Gateway WebSocket for push-based streaming updates
 * (near-zero latency). HTTP polling at 500ms is a FALLBACK, used only when the
 * socket is genuinely broken:
 * - WebSocket URL is not configured
 * - WebSocket connection fails or is unavailable
 * - The socket stops answering heartbeats (half-open / dead)
 * - `expectPushes` is set (a turn is generating) and no push has arrived for
 *   PUSH_STALL_MS — the push pipeline behind a healthy socket is degraded
 *
 * An idle-but-healthy socket is NOT a fallback case: `wsConnected` stays true
 * and the redundant poll stays off.
 *
 * Usage:
 * ```tsx
 * const { streamingState, wsConnected, usingFallback } = useAskPunttStream(
 *   chatId, streamingMessageId, { enabled: shouldStream, expectPushes: generating }
 * );
 * ```
 */
export function useAskPunttStream(
  chatId: string,
  messageId: string | null,
  options?: { enabled?: boolean; expectPushes?: boolean },
): AskPunttStreamResult {
  const enabled = options?.enabled !== false && !!chatId && !!messageId;
  // A turn is generating, so pushes are DUE. Read through a ref inside the
  // health-check closure so flipping it never re-runs the effect (which would
  // re-subscribe and churn the socket).
  const expectPushesRef = useRef(options?.expectPushes === true);
  expectPushesRef.current = options?.expectPushes === true;
  const [wsConnected, setWsConnected] = useState(false);
  const [streamingState, setStreamingState] = useState<StreamingState | null>(
    null,
  );
  const [usingFallback, setUsingFallback] = useState(false);
  const [lastWsPushAt, setLastWsPushAt] = useState<number | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether the fallback loop is running. Separate from the timer handle: the
  // loop self-schedules, so between a tick firing and the next `setTimeout` the
  // handle is stale-but-truthy and can't be used as the "already polling" test.
  const pollActiveRef = useRef(false);
  // Consecutive failed polls, used to back the interval off.
  const pollFailuresRef = useRef(0);
  // Mirror of `usingFallback` the effect can read/write WITHOUT being a
  // dependency — keeps the main effect from re-running (re-subscribe + new
  // timers) every time the fallback flag flips during a flapping WS.
  const usingFallbackRef = useRef(false);
  // Track the last wsConnected value to detect transitions
  const wasConnectedRef = useRef(false);
  // Timestamp of the last WS push (ref so the health-check interval reads it
  // without being re-created). `wsConnected` means OPEN *and* fresh per this.
  const lastPushRef = useRef<number | null>(null);

  const setFallback = useCallback((value: boolean) => {
    usingFallbackRef.current = value;
    setUsingFallback(value);
  }, []);

  const clearPolling = useCallback(() => {
    pollActiveRef.current = false;
    pollFailuresRef.current = 0;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const startPollingFallback = useCallback(() => {
    if (!chatId || !messageId) return;
    // Already polling — don't stack a second loop.
    if (pollActiveRef.current) return;
    pollActiveRef.current = true;
    pollFailuresRef.current = 0;
    setFallback(true);

    // Self-scheduling timeout rather than a fixed setInterval so the delay can
    // grow while /stream keeps failing. A flat 500ms interval polled forever at
    // 2 rps whenever the snapshot was absent — which is the normal state before
    // the turn's first flush, and a permanent one if the turn stalls or the
    // snapshot's 2h TTL lapses while the message is still `pending`.
    const nextDelayMs = () => {
      const excess = pollFailuresRef.current - POLL_FAILURES_BEFORE_BACKOFF;
      if (excess <= 0) return POLL_BASE_MS;
      return Math.min(POLL_BASE_MS * 2 ** excess, POLL_MAX_MS);
    };

    const schedule = () => {
      if (!pollActiveRef.current) return;
      pollTimeoutRef.current = setTimeout(() => void poll(), nextDelayMs());
    };

    const poll = async () => {
      if (!pollActiveRef.current) return;
      try {
        const state = await getMessageStreamingState(chatId, messageId);
        // The loop may have been torn down while the request was in flight.
        if (!pollActiveRef.current) return;
        pollFailuresRef.current = 0;
        if (state) {
          setStreamingState(state as StreamingState);
          // Polling produced the latest value — clear WS freshness.
          setLastWsPushAt(null);
        }
      } catch {
        // Expected while the snapshot has not been published yet (/stream 404s
        // until it lands). Counted rather than ignored so a snapshot that never
        // lands backs off instead of polling for the life of the tab.
        pollFailuresRef.current += 1;
      }
      schedule();
    };

    // Fire immediately, then self-schedule.
    void poll();
  }, [chatId, messageId, setFallback]);

  useEffect(() => {
    if (!enabled) {
      clearPolling();
      setStreamingState(null);
      setWsConnected(false);
      setFallback(false);
      setLastWsPushAt(null);
      wasConnectedRef.current = false;
      lastPushRef.current = null;
      return;
    }

    // Baseline for the push-stall guard: a turn that has produced no push
    // since this effect started is only "stalled" once PUSH_STALL_MS elapses,
    // never immediately.
    const effectStartedAt = Date.now();

    const wsUrl = getWsUrl();
    if (!wsUrl) {
      // No WebSocket URL configured — use polling fallback
      startPollingFallback();
      return () => {
        clearPolling();
      };
    }

    // Attempt WebSocket connection
    const onUpdate = (msg: StreamUpdateMessage) => {
      lastPushRef.current = Date.now();
      setWsConnected(true);
      wasConnectedRef.current = true;
      setStreamingState(msg.payload);
      setLastWsPushAt(Date.now());
      // The socket just proved itself — stop the polling fallback immediately
      // rather than waiting for the next health check.
      if (usingFallbackRef.current) {
        clearPolling();
        setFallback(false);
      }
    };

    const unsubscribe = subscribeWs(chatId, messageId, onUpdate);

    // Fallback timer: if no push has arrived within 2.5s of enabling, start
    // polling as a safety net. Keyed on "no push yet" (not socket state) so an
    // OPEN-but-silent socket still gets covered by the poll until data flows.
    const fallbackTimer = setTimeout(() => {
      if (lastPushRef.current == null) {
        startPollingFallback();
      }
    }, 2500);

    // Periodically check WS health. "Connected" means the socket is OPEN and
    // ALIVE — it answered recently. Liveness is proven by the heartbeat, not by
    // push traffic, so an idle socket between/after turns stays connected and
    // the redundant HTTP poll stays off. Only a socket that has stopped
    // answering (or is closed / never handshook) flips `wsConnected` false, so
    // the poll is a fallback for a BROKEN socket — which is what it is for.
    const healthCheck = setInterval(() => {
      const open = _globalWs?.readyState === WebSocket.OPEN;
      // Socket-level liveness: OPEN, handshook, and answering heartbeats.
      const socketAlive =
        open &&
        _globalWsReady &&
        _lastFrameAt != null &&
        Date.now() - _lastFrameAt <= HEARTBEAT_TIMEOUT_MS;
      // Pipeline-level: while a turn is generating, a healthy socket that has
      // seen no push for PUSH_STALL_MS means the push path behind it is
      // degraded, so the poll must take over. Baseline is the LATER of the last
      // push and this effect's start, so a stale push timestamp from a previous
      // turn doesn't read as a stall the instant a new turn begins.
      const pushBaseline = Math.max(lastPushRef.current ?? 0, effectStartedAt);
      const pushStalled =
        expectPushesRef.current && Date.now() - pushBaseline > PUSH_STALL_MS;
      const alive = socketAlive && !pushStalled;
      if (alive) {
        setWsConnected(true);
        wasConnectedRef.current = true;
        if (usingFallbackRef.current) {
          clearPolling();
          setFallback(false);
        }
      } else {
        setWsConnected(false);
        // Socket is down, never handshook, or stopped answering heartbeats
        // (the real half-open case) — re-arm polling so updates keep flowing.
        if (!pollActiveRef.current) {
          startPollingFallback();
        }
      }
    }, HEALTH_CHECK_MS);

    return () => {
      clearTimeout(fallbackTimer);
      clearInterval(healthCheck);
      clearPolling();
      unsubscribe();
      wasConnectedRef.current = false;
    };
    // `usingFallback` is intentionally NOT a dependency — the effect manages
    // the socket + timers for a given (enabled, chatId, messageId) and reads
    // the live fallback flag via usingFallbackRef. Including it would tear the
    // socket down and re-subscribe on every fallback flip.
  }, [
    enabled,
    chatId,
    messageId,
    startPollingFallback,
    clearPolling,
    setFallback,
  ]);

  return { streamingState, wsConnected, usingFallback, lastWsPushAt };
}
